import * as yup from 'yup';
import { INT32_MAX_NUMBER } from '../constants/common';
import { REGEXES } from '../formValidation/validation.constants';
import {
  EntityIdValidation,
  ExternalReferencesValidation,
  KeyValuePairValidation,
  LabelValuePairValidation,
  OneOfEnumValidation,
  PercentageValidation,
  TelephoneNumberValidation,
  UniqueArrayValidation,
  UniqueArrayWithMultipleKeysValidation
} from './yup.validations.interfaces';

const entityIdSchema = yup.number().integer().positive().max(INT32_MAX_NUMBER);
const externalReferenceSchema = yup.object({
  Type: yup.string().label('Type').required(),
  Value: yup.string().label('Value').required()
});
const keyValuePairSchema = yup
  .object({
    label: yup.mixed().label('Key'),
    value: yup.mixed().label('Value')
  })
  .required();
const labelEntityIdPairSchema = yup
  .object({
    label: yup.string().label('Label'),
    value: entityIdSchema.label('Value')
  })
  .required();
const labelValuePairSchema = yup
  .object({
    label: yup.string().label('Label'),
    value: yup.mixed().label('Value')
  })
  .required();

/** Validate the provided value is an entity (e.g. customer, pricing plan, or offer) ID. */
export function entityId(this: yup.NumberSchema, options?: EntityIdValidation) {
  return this.test({
    name: 'entityId',
    message: options?.errorMessage || '${path} is not a valid ID value',
    test: function testValue(value) {
      if (value) {
        // @ts-ignore error after yup update
        const valueAsInteger = Number.parseInt(value, 10);

        return Number.isNaN(valueAsInteger)
          ? false
          : entityIdSchema.isValidSync(valueAsInteger);
      }

      return true;
    }
  });
}

/** Validate the provided value is a list of external references. */
export function externalReferences(
  this: yup.ArraySchema<{ Type: string; Value: string }[], any>,
  {
    errorMessage = '',
    minItems = 0,
    referenceSchema
  }: ExternalReferencesValidation = {}
) {
  return this.test({
    name: 'externalReference',
    message: errorMessage || '${path} is not valid',
    params: {},
    test: function testValue(value) {
      const externalReferencesSchema = yup
        .array()
        .of(referenceSchema || externalReferenceSchema)
        .min(minItems);

      return externalReferencesSchema.isValidSync(value);
    }
  });
}

/** Validate a `Key` and `Value` pair. */
export function keyValuePair<KeyType, ValueType = string>(
  this: yup.ObjectSchema<{ Key: KeyType; Value: ValueType }>,
  { errorMessage = '' }: KeyValuePairValidation = {}
) {
  return this.test({
    name: 'keyValuePair',
    message: errorMessage || '${path} is not valid',
    params: {},
    test: function testValue(value) {
      return keyValuePairSchema.isValidSync(value);
    }
  });
}

/** Validate a `label` and `value` pair. */
export function labelValuePair<ValueType = any>(
  this: yup.ObjectSchema<{ label: string; value: ValueType }>,
  { errorMessage = '', isValueEntityId = false }: LabelValuePairValidation = {}
) {
  return this.test({
    name: 'labelValuePair',
    message: errorMessage || '${path} is not valid',
    params: {},
    test: function testValue(value) {
      if (isValueEntityId) {
        return labelEntityIdPairSchema.isValidSync(value);
      }

      return labelValuePairSchema.isValidSync(value);
    }
  });
}

/** Validate the provided value is in the provided enum. */
export function oneOfEnum(
  this: yup.MixedSchema<any>,
  { enumType, errorMessage = '' }: OneOfEnumValidation
) {
  return this.test({
    name: 'oneOfEnum',
    message: errorMessage || '${path} is invalid',
    test: function testValue(value) {
      if (value === null || value === undefined) {
        return true;
      }

      return value in enumType;
    }
  });
}

/** Validate the provided value is a valid percentage. */
export function percentage(
  this: yup.NumberSchema,
  { errorMessage = '' }: PercentageValidation = {}
) {
  return this.test({
    name: 'percentage',
    message: errorMessage || '${path} must be a valid percentage',
    test: function testValue(value) {
      const schema = yup.number().required().min(0).max(100);

      return schema.isValidSync(value);
    }
  });
}

/** Validate the provided value is a telephone number. */
export function telephoneNumber(
  this: yup.StringSchema,
  { errorMessage = '' }: TelephoneNumberValidation = {}
) {
  return this.test({
    name: 'telephoneNumber',
    message:
      errorMessage ||
      '${path} must be a valid telephone number (e.g. +12223334444 or 2223334444)',
    test: function testValue(value) {
      const schema = yup.string().matches(REGEXES.PHONE_NUMBER);

      return schema.isValidSync(value);
    }
  });
}

export function uniqueArray(
  this: yup.ArraySchema<any, any>,
  options?: UniqueArrayValidation
) {
  return this.test({
    name: 'uniqueArray',
    message:
      options?.errorMessage ||
      'Duplicate ${path}. Please remove the duplicates.',
    test: function testValue(value: any[]) {
      return (
        !value ||
        value.length ===
          [
            ...new Set(
              options?.key ? value.map((item) => item[options.key!]) : value
            )
          ].length
      );
    }
  });
}

export function uniqueArrayWithMultipleKeys(
  this: yup.ArraySchema<any, any>,
  options?: UniqueArrayWithMultipleKeysValidation
) {
  return this.test({
    name: 'uniqueArrayWithMultipleKeys',
    message:
      options?.errorMessage ||
      'Duplicate ${path}. Please remove the duplicates.',
    test: function testValue(value: any[]) {
      const valueLength = (value || []).map(
        (item) => item[options?.keys?.[0]!]
      ).length;

      if (valueLength <= 1) return true;

      const uniqueArrayItems = (options?.keys || []).map(
        (key) =>
          [
            ...new Set(
              key
                ? (value || []).map((item) =>
                    item[key] !== null && item[key] !== undefined
                      ? item[key]
                      : ''
                  )
                : value
            )
          ].length
      );

      return [...new Set(uniqueArrayItems)]?.length !== 1;
    }
  });
}
